import { Logger } from "../Misc/logger";
import { Observable } from "../Misc/observable";
import { InternalTexture, InternalTextureSource } from "../Materials/Textures/internalTexture";
import { RenderTargetTexture } from "../Materials/Textures/renderTargetTexture";
import { WebXRManagedOutputCanvas, WebXRManagedOutputCanvasOptions } from "./webXRManagedOutputCanvas";
/**
 * Manages an XRSession to work with Babylon's engine
 * @see https://doc.babylonjs.com/how_to/webxr_session_manager
 */
var WebXRSessionManager = /** @class */ (function () {
    /**
     * Constructs a WebXRSessionManager, this must be initialized within a user action before usage
     * @param scene The scene which the session should be created for
     */
    function WebXRSessionManager(
    /** The scene which the session should be created for */
    scene) {
        this.scene = scene;
        this._sessionEnded = false;
        this.baseLayer = null;
        /** WebXR timestamp updated every frame */
        this.currentTimestamp = -1;
        /**
         * Used just in case of a failure to initialize an immersive session.
         * The viewer reference space is compensated using this height, creating a kind of "viewer-floor" reference space
         */
        this.defaultHeightCompensation = 1.7;
        /**
         * Fires every time a new xrFrame arrives which can be used to update the camera
         */
        this.onXRFrameObservable = new Observable();
        /**
         * Fires when the reference space changed
         */
        this.onXRReferenceSpaceChanged = new Observable();
        /**
         * Fires when the xr session is ended either by the device or manually done
         */
        this.onXRSessionEnded = new Observable();
        /**
         * Fires when the xr session is ended either by the device or manually done
         */
        this.onXRSessionInit = new Observable();
    }
    Object.defineProperty(WebXRSessionManager.prototype, "referenceSpace", {
        /**
         * The current reference space used in this session. This reference space can constantly change!
         * It is mainly used to offset the camera's position.
         */
        get: function () {
            return this._referenceSpace;
        },
        /**
         * Set a new reference space and triggers the observable
         */
        set: function (newReferenceSpace) {
            this._referenceSpace = newReferenceSpace;
            this.onXRReferenceSpaceChanged.notifyObservers(this._referenceSpace);
        },
        enumerable: false,
        configurable: true
    });
    /**
     * Disposes of the session manager
     */
    WebXRSessionManager.prototype.dispose = function () {
        // disposing without leaving XR? Exit XR first
        if (!this._sessionEnded) {
            this.exitXRAsync();
        }
        this.onXRFrameObservable.clear();
        this.onXRSessionEnded.clear();
        this.onXRReferenceSpaceChanged.clear();
        this.onXRSessionInit.clear();
    };
    /**
     * Stops the xrSession and restores the render loop
     * @returns Promise which resolves after it exits XR
     */
    WebXRSessionManager.prototype.exitXRAsync = function () {
        if (this.session && !this._sessionEnded) {
            this._sessionEnded = true;
            return this.session.end().catch(function (e) {
                Logger.Warn("Could not end XR session.");
            });
        }
        return Promise.resolve();
    };
    /**
     * Gets the correct render target texture to be rendered this frame for this eye
     * @param eye the eye for which to get the render target
     * @returns the render target for the specified eye
     */
    WebXRSessionManager.prototype.getRenderTargetTextureForEye = function (eye) {
        return this._rttProvider.getRenderTargetForEye(eye);
    };
    /**
     * Creates a WebXRRenderTarget object for the XR session
     * @param onStateChangedObservable optional, mechanism for enabling/disabling XR rendering canvas, used only on Web
     * @param options optional options to provide when creating a new render target
     * @returns a WebXR render target to which the session can render
     */
    WebXRSessionManager.prototype.getWebXRRenderTarget = function (options) {
        var engine = this.scene.getEngine();
        if (this._xrNavigator.xr.native) {
            return this._xrNavigator.xr.getWebXRRenderTarget(engine);
        }
        else {
            options = options || WebXRManagedOutputCanvasOptions.GetDefaults(engine);
            options.canvasElement = engine.getRenderingCanvas() || undefined;
            return new WebXRManagedOutputCanvas(this, options);
        }
    };
    /**
     * Initializes the manager
     * After initialization enterXR can be called to start an XR session
     * @returns Promise which resolves after it is initialized
     */
    WebXRSessionManager.prototype.initializeAsync = function () {
        // Check if the browser supports webXR
        this._xrNavigator = navigator;
        if (!this._xrNavigator.xr) {
            return Promise.reject("WebXR not available");
        }
        return Promise.resolve();
    };
    /**
     * Initializes an xr session
     * @param xrSessionMode mode to initialize
     * @param xrSessionInit defines optional and required values to pass to the session builder
     * @returns a promise which will resolve once the session has been initialized
     */
    WebXRSessionManager.prototype.initializeSessionAsync = function (xrSessionMode, xrSessionInit) {
        var _this = this;
        if (xrSessionMode === void 0) { xrSessionMode = "immersive-vr"; }
        if (xrSessionInit === void 0) { xrSessionInit = {}; }
        return this._xrNavigator.xr.requestSession(xrSessionMode, xrSessionInit).then(function (session) {
            _this.session = session;
            _this.onXRSessionInit.notifyObservers(session);
            _this._sessionEnded = false;
            // handle when the session is ended (By calling session.end or device ends its own session eg. pressing home button on phone)
            _this.session.addEventListener("end", function () {
                var engine = _this.scene.getEngine();
                _this._sessionEnded = true;
                // Remove render target texture and notify frame observers
                _this._rttProvider = null;
                // make sure dimensions object is restored
                engine.framebufferDimensionsObject = null;
                // Restore frame buffer to avoid clear on xr framebuffer after session end
                engine.restoreDefaultFramebuffer();
                // Need to restart render loop as after the session is ended the last request for new frame will never call callback
                engine.customAnimationFrameRequester = null;
                _this.onXRSessionEnded.notifyObservers(null);
                engine._renderLoop();
            }, { once: true });
            return _this.session;
        });
    };
    /**
     * Checks if a session would be supported for the creation options specified
     * @param sessionMode session mode to check if supported eg. immersive-vr
     * @returns A Promise that resolves to true if supported and false if not
     */
    WebXRSessionManager.prototype.isSessionSupportedAsync = function (sessionMode) {
        return WebXRSessionManager.IsSessionSupportedAsync(sessionMode);
    };
    /**
     * Resets the reference space to the one started the session
     */
    WebXRSessionManager.prototype.resetReferenceSpace = function () {
        this.referenceSpace = this.baseReferenceSpace;
    };
    /**
     * Starts rendering to the xr layer
     */
    WebXRSessionManager.prototype.runXRRenderLoop = function () {
        var _this = this;
        var engine = this.scene.getEngine();
        // Tell the engine's render loop to be driven by the xr session's refresh rate and provide xr pose information
        engine.customAnimationFrameRequester = {
            requestAnimationFrame: this.session.requestAnimationFrame.bind(this.session),
            renderFunction: function (timestamp, xrFrame) {
                if (_this._sessionEnded) {
                    return;
                }
                // Store the XR frame and timestamp in the session manager
                _this.currentFrame = xrFrame;
                _this.currentTimestamp = timestamp;
                if (xrFrame) {
                    engine.framebufferDimensionsObject = _this.baseLayer;
                    _this.onXRFrameObservable.notifyObservers(xrFrame);
                    engine._renderLoop();
                    engine.framebufferDimensionsObject = null;
                }
            },
        };
        if (this._xrNavigator.xr.native) {
            this._rttProvider = this._xrNavigator.xr.getNativeRenderTargetProvider(this.session, this._createRenderTargetTexture.bind(this));
        }
        else {
            // Create render target texture from xr's webgl render target
            var rtt_1 = this._createRenderTargetTexture(this.baseLayer.framebufferWidth, this.baseLayer.framebufferHeight, this.baseLayer.framebuffer);
            this._rttProvider = { getRenderTargetForEye: function () { return rtt_1; } };
            engine.framebufferDimensionsObject = this.baseLayer;
        }
        // Stop window's animation frame and trigger sessions animation frame
        if (typeof window !== "undefined" && window.cancelAnimationFrame) {
            window.cancelAnimationFrame(engine._frameHandler);
        }
        engine._renderLoop();
    };
    /**
     * Sets the reference space on the xr session
     * @param referenceSpaceType space to set
     * @returns a promise that will resolve once the reference space has been set
     */
    WebXRSessionManager.prototype.setReferenceSpaceTypeAsync = function (referenceSpaceType) {
        var _this = this;
        if (referenceSpaceType === void 0) { referenceSpaceType = "local-floor"; }
        return this.session
            .requestReferenceSpace(referenceSpaceType)
            .then(function (referenceSpace) {
            return referenceSpace;
        }, function (rejectionReason) {
            Logger.Error("XR.requestReferenceSpace failed for the following reason: ");
            Logger.Error(rejectionReason);
            Logger.Log('Defaulting to universally-supported "viewer" reference space type.');
            return _this.session.requestReferenceSpace("viewer").then(function (referenceSpace) {
                var heightCompensation = new XRRigidTransform({ x: 0, y: -_this.defaultHeightCompensation, z: 0 });
                return referenceSpace.getOffsetReferenceSpace(heightCompensation);
            }, function (rejectionReason) {
                Logger.Error(rejectionReason);
                throw 'XR initialization failed: required "viewer" reference space type not supported.';
            });
        })
            .then(function (referenceSpace) {
            // create viewer reference space before setting the first reference space
            return _this.session.requestReferenceSpace("viewer").then(function (viewerReferenceSpace) {
                _this.viewerReferenceSpace = viewerReferenceSpace;
                return referenceSpace;
            });
        })
            .then(function (referenceSpace) {
            // initialize the base and offset (currently the same)
            _this.referenceSpace = _this.baseReferenceSpace = referenceSpace;
            return _this.referenceSpace;
        });
    };
    /**
     * Updates the render state of the session
     * @param state state to set
     * @returns a promise that resolves once the render state has been updated
     */
    WebXRSessionManager.prototype.updateRenderStateAsync = function (state) {
        if (state.baseLayer) {
            this.baseLayer = state.baseLayer;
        }
        return this.session.updateRenderState(state);
    };
    /**
     * Returns a promise that resolves with a boolean indicating if the provided session mode is supported by this browser
     * @param sessionMode defines the session to test
     * @returns a promise with boolean as final value
     */
    WebXRSessionManager.IsSessionSupportedAsync = function (sessionMode) {
        if (!navigator.xr) {
            return Promise.resolve(false);
        }
        // When the specs are final, remove supportsSession!
        var functionToUse = navigator.xr.isSessionSupported || navigator.xr.supportsSession;
        if (!functionToUse) {
            return Promise.resolve(false);
        }
        else {
            return functionToUse
                .call(navigator.xr, sessionMode)
                .then(function (result) {
                var returnValue = typeof result === "undefined" ? true : result;
                return Promise.resolve(returnValue);
            })
                .catch(function (e) {
                Logger.Warn(e);
                return Promise.resolve(false);
            });
        }
    };
    WebXRSessionManager.prototype._createRenderTargetTexture = function (width, height, framebuffer) {
        if (framebuffer === void 0) { framebuffer = null; }
        // Create internal texture
        var internalTexture = new InternalTexture(this.scene.getEngine(), InternalTextureSource.Unknown, true);
        internalTexture.width = width;
        internalTexture.height = height;
        internalTexture._framebuffer = framebuffer;
        // Create render target texture from the internal texture
        var renderTargetTexture = new RenderTargetTexture("XR renderTargetTexture", { width: width, height: height }, this.scene, undefined, undefined, undefined, undefined, undefined, undefined, undefined, undefined, undefined, true);
        renderTargetTexture._texture = internalTexture;
        return renderTargetTexture;
    };
    return WebXRSessionManager;
}());
export { WebXRSessionManager };
//# sourceMappingURL=webXRSessionManager.js.map